<template>
  <div class="form-panel">
    <PanelHeader :titleText="$t(LABEL_REGISTRATION_EMAIL_VERIFY_TITLE)" :displayBack="true" @onBack="$emit('onBack')" />

    <div class="subtitle" v-html="subTitleLabel"></div>
    <div>
      <div class="form-group" style="margin-bottom: 0">
        <input
          type="text"
          v-bind:class="{ 'is-danger': isCodeInvalid }"
          :placeholder="codePlaceHolder"
          @keyup="onCodeKeyPress"
          class="form-control code-input"
          v-model.trim="code"
          tabindex="0"
          autocomplete="off"
          spellcheck="false"
          autocapitalize="none"
          :maxlength="codeLength"
          ref="codeField"
          v-on:keyup.enter="verify"
        />
        <div class="help" v-bind:class="{ 'is-danger': isCodeInvalid }" v-html="codeMessageLabel"></div>
      </div>
    </div>
    <div class="btn-panel">
      <a
        class="btn btn-full"
        tabindex="0"
        v-bind:class="{ 'is-disabled': processing, 'is-loading': processing }"
        @keyup.enter="verify"
        @click="verify"
      >
        {{ $t(LABEL_SEND) }}
      </a>
    </div>

    <div style="margin-top: 2.2rem" v-if="customerEmail">
      <a href="#" @click.prevent="displayModal">{{ $t(LABEL_REGISTRATION_EMAIL_NOT_RECEIVED) }}</a>
    </div>

    <transition name="fade">
      <div class="modal-background" v-if="this.isModalActived" @click="this.isModalActived = false">
        <div class="modal-popup" @click.stop="">
          <div class="help-panel">
            <div style="margin-bottom: 1rem; text-align: center">
              <b style="font-size: 1.1rem">{{ $t(LABEL_REGISTRATION_EMAIL_NOT_RECEIVED) }}</b>
            </div>
            <ul>
              <li>
                {{ $t(LABEL_REGISTRATION_ENTERED_INVALID_EMAIL) }}
                <br />
                {{ this.email }} -
                <a
                  @click="
                    isModalActived = false;
                    $emit('onBack');
                  "
                  style="cursor: pointer"
                  >{{ $t(LABEL_REGISTRATION_EDIT_EMAIL) }}</a
                >
              </li>
              <li>
                {{ $t(LABEL_REGISTRATION_EMAIL_SPAM_FOLDER) }} {{ $t(LABE_REGISTRATION_EMAIL_WHITE_LIST, [customerDomain, customerEmail]) }}
                <br />
                <a
                  @click="
                    isModalActived = false;
                    $emit('resendCode');
                  "
                  style="cursor: pointer"
                  >{{ $t(LABE_REGISTRATION_RESEND_CODE) }}</a
                >
              </li>
            </ul>
          </div>
          <a
            ref="btnHelpPanel"
            class="btn btn-full"
            style="margin-top: 1.2rem"
            tabindex="0"
            @click="this.isModalActived = false"
            @keypress.prevent="this.isModalActived = false"
            >{{ $t(LABEL_CLOSE) }}</a
          >
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, PropType, ref, toRef, watchEffect } from 'vue';

import { ServerValidationError } from '@/libs/registrationState';
import { FormLabel, translateFormLabel } from '@/libs/form';
import {
  LABEL_REGISTRATION_EMAIL_SENT,
  LABEL_REGISTRATION_ENTER_CODE,
  LABEL_REGISTRATION_INVALID_CODE,
  LABEL_SEND,
  LABEL_REGISTRATION_EMAIL_VERIFY_TITLE,
  LABEL_REGISTRATION_EMAIL_NOT_RECEIVED,
  LABEL_REGISTRATION_EDIT_EMAIL,
  LABEL_CLOSE,
  LABEL_REGISTRATION_ENTERED_INVALID_EMAIL,
  LABE_REGISTRATION_RESEND_CODE,
  LABEL_REGISTRATION_EMAIL_SPAM_FOLDER,
  LABE_REGISTRATION_EMAIL_WHITE_LIST,
} from '@/libs/i18nLabels';
import PanelHeader from './panel-header.vue';
import focus from '../libs/focus';

export default defineComponent({
  components: {
    PanelHeader,
  },

  props: {
    email: { type: String, required: true },
    codeLength: { type: Number, required: true },
    customerEmail: { type: String, required: false },
    validationErrors: {
      type: Object as PropType<ServerValidationError[]>,
    },

    processing: { type: Boolean },
  },

  emits: ['codeSubmitted', 'onBack', 'resendCode'],

  setup(props) {
    const isCodeInvalid = ref(false);
    const codeMessage = ref<FormLabel | null>(null);
    const isModalActived = ref(false);

    const btnHelpPanel = ref<HTMLElement | null>(null);

    // server validation errors

    function displayServerValidationError(err: ServerValidationError) {
      let { field = '' } = err;

      // Lower case field name
      field = field.toLowerCase();

      switch (field) {
        case 'code':
          isCodeInvalid.value = true;
          codeMessage.value = { label: LABEL_REGISTRATION_INVALID_CODE };
          break;

        default:
          break;
      }
    }

    const validationErrors = toRef(props, 'validationErrors');
    watchEffect(() => {
      if (validationErrors.value) {
        validationErrors.value.forEach((validationError) => {
          displayServerValidationError(validationError);
        });
      }
    });

    function displayModal() {
      isModalActived.value = true;

      nextTick(() => {
        focus(btnHelpPanel.value);
      });
    }

    return {
      isCodeInvalid,
      codeMessage,
      isModalActived,
      btnHelpPanel,
      displayModal,
      LABEL_SEND,
      LABEL_REGISTRATION_EMAIL_VERIFY_TITLE,
      LABEL_REGISTRATION_EMAIL_NOT_RECEIVED,
      LABEL_REGISTRATION_EDIT_EMAIL,
      LABEL_REGISTRATION_ENTERED_INVALID_EMAIL,
      LABEL_REGISTRATION_EMAIL_SPAM_FOLDER,
      LABE_REGISTRATION_EMAIL_WHITE_LIST,
      LABE_REGISTRATION_RESEND_CODE,
      LABEL_CLOSE,
    };
  },

  data() {
    return {
      code: '',
    };
  },

  mounted() {
    focus(this.$refs.codeField);
  },

  methods: {
    onCodeKeyPress() {
      this.isCodeInvalid = false;
      this.codeMessage = null;
    },

    async verify() {
      if (this.processing) {
        return;
      }

      if (this.code.length !== this.codeLength) {
        this.isCodeInvalid = true;
        this.codeMessage = { label: LABEL_REGISTRATION_INVALID_CODE };
        return;
      }

      this.codeMessage = null;
      this.isCodeInvalid = false;

      this.$emit('codeSubmitted', this.code);
    },

    translate(formLabel: FormLabel) {
      return translateFormLabel(formLabel, this.$t);
    },
  },

  computed: {
    customerDomain(): string {
      if (!this.customerEmail) {
        return '';
      }
      return this.customerEmail.split('@')[1];
    },

    subTitleLabel(): string {
      return this.$t(LABEL_REGISTRATION_EMAIL_SENT, [`<b>${this.email}</b>`]).toString();
    },

    codePlaceHolder() {
      return this.$t(LABEL_REGISTRATION_ENTER_CODE).toString();
    },

    codeMessageLabel() {
      if (!this.codeMessage) {
        return '&nbsp';
      }
      return translateFormLabel(this.codeMessage, this.$t);
    },
  },
});
</script>

<style lang="scss" scoped>
.help {
  text-align: center;
}

.subtitle {
  margin-bottom: 1.2rem;
}

.code-input {
  text-align: center;

  display: inline-block;
}
.btn-panel {
  margin-top: 0 !important;
}

.modal-background {
  background: rgba(8, 8, 8, 0.75);
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.modal-popup {
  padding: 1.5rem;
  background: #fff;
  border-radius: 8px;
  max-width: 500px;
  overflow: auto;
  max-height: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.help-panel {
  text-align: left;
  font-weight: bold;

  ::v-deep(ul) {
    font-weight: normal !important;
    margin-top: 10px;
    li {
      margin: 5px 0px !important;
      margin-left: 20px !important;
    }
  }
}
</style>
